import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserShield,
  faUserTie,
  faSearch,
  faSpinner,
  faExclamationTriangle,
  faCheckCircle,
  faTimesCircle,
  faCrown,
  faUser,
  faRefresh,
} from '@fortawesome/free-solid-svg-icons';
import api from '../axiosInstance';
import '../AdminPanel.css';

function AdminPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [updatingUserId, setUpdatingUserId] = useState(null);

  // Get current logged in user from localStorage to prevent self-demotion
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/admin/users', {
        params: searchTerm ? { search: searchTerm } : {},
      });
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.response?.data?.message || 'Failed to fetch user list.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchUsers();
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const handleRoleChange = async (userId, newRole) => {
    if (userId === currentUser.id && newRole !== 'admin') {
      setError('You cannot demote yourself from admin role.');
      return;
    }

    try {
      setUpdatingUserId(userId);
      setError(null);
      setSuccessMessage('');

      const response = await api.put(`/admin/users/${userId}/role`, {
        role: newRole,
      });

      setSuccessMessage(response.data.message || `User role updated to ${newRole}.`);
      setUsers((prevUsers) =>
        prevUsers.map((u) => (u._id === userId ? { ...u, role: newRole } : u))
      );

      setTimeout(() => setSuccessMessage(''), 4000);
    } catch (err) {
      console.error('Error updating role:', err);
      setError(err.response?.data?.message || 'Failed to update user role.');
    } finally {
      setUpdatingUserId(null);
    }
  };

  return (
    <div className="ap-container">
      {/* Header Banner */}
      <div className="ap-header">
        <div className="ap-header-content">
          <div className="ap-header-title">
            <FontAwesomeIcon icon={faUserShield} className="ap-header-icon" />
            <div>
              <h1>Admin User Management</h1>
              <p>Manage platform users, roles, and administrative permissions</p>
            </div>
          </div>
          <button className="ap-refresh-btn" onClick={fetchUsers} title="Refresh User List">
            <FontAwesomeIcon icon={faRefresh} spin={loading} /> Refresh
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="ap-content">
        {/* Search Bar & Stats */}
        <div className="ap-toolbar">
          <div className="ap-search-box">
            <FontAwesomeIcon icon={faSearch} className="ap-search-icon" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="ap-search-input"
            />
            {searchTerm && (
              <button className="ap-search-clear" onClick={() => setSearchTerm('')}>
                &times;
              </button>
            )}
          </div>
          <div className="ap-stats">
            <span className="ap-stat-badge">
              <strong>{users.length}</strong> {users.length === 1 ? 'User' : 'Total Users'}
            </span>
            <span className="ap-stat-badge admin-count">
              <strong>{users.filter((u) => u.role === 'admin').length}</strong> Admins
            </span>
            <span className="ap-stat-badge hr-count">
              <strong>{users.filter((u) => u.role === 'hr').length}</strong> HR Users
            </span>
          </div>
        </div>

        {/* Notifications */}
        {error && (
          <div className="ap-alert ap-alert-error">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span>{error}</span>
          </div>
        )}

        {successMessage && (
          <div className="ap-alert ap-alert-success">
            <FontAwesomeIcon icon={faCheckCircle} />
            <span>{successMessage}</span>
          </div>
        )}

        {/* User Table */}
        <div className="ap-table-wrapper">
          {loading && !users.length ? (
            <div className="ap-loading-state">
              <FontAwesomeIcon icon={faSpinner} spin className="ap-spinner" />
              <p>Loading users...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="ap-empty-state">
              <FontAwesomeIcon icon={faUserTie} className="ap-empty-icon" />
              <h3>No users found</h3>
              <p>{searchTerm ? `No users matching "${searchTerm}"` : 'No registered users in the database.'}</p>
            </div>
          ) : (
            <table className="ap-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Role</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const isSelf = user._id === currentUser.id;
                  const isUpdating = updatingUserId === user._id;

                  return (
                    <tr key={user._id} className={isSelf ? 'ap-row-self' : ''}>
                      <td className="ap-cell-user">
                        <div className="ap-avatar">
                          {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                        </div>
                        <div className="ap-user-info">
                          <span className="ap-user-name">
                            {user.name} {isSelf && <span className="ap-you-tag">(You)</span>}
                          </span>
                        </div>
                      </td>
                      <td className="ap-cell-email">{user.email}</td>
                      <td>
                        {user.isVerified ? (
                          <span className="ap-badge ap-badge-verified">
                            <FontAwesomeIcon icon={faCheckCircle} /> Verified
                          </span>
                        ) : (
                          <span className="ap-badge ap-badge-pending">
                            <FontAwesomeIcon icon={faTimesCircle} /> Unverified
                          </span>
                        )}
                      </td>
                      <td>
                        <span className={`ap-badge ap-badge-role ${user.role}`}>
                          <FontAwesomeIcon icon={user.role === 'admin' ? faCrown : faUser} />{' '}
                          {user.role ? user.role.toUpperCase() : 'HR'}
                        </span>
                      </td>
                      <td className="ap-cell-actions">
                        {isUpdating ? (
                          <FontAwesomeIcon icon={faSpinner} spin />
                        ) : (
                          <select
                            value={user.role || 'hr'}
                            onChange={(e) => handleRoleChange(user._id, e.target.value)}
                            disabled={isSelf}
                            className="ap-role-select"
                            title={isSelf ? 'You cannot demote yourself' : 'Change user role'}
                          >
                            <option value="hr">HR User</option>
                            <option value="admin">Admin</option>
                          </select>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminPanel;
